import { useMemo } from 'react';
import { Space, Table } from 'antd';
import { Answers } from './OhatResultGraph';
import { Question } from '../../../constants/question';
import { RESULT_COLORS } from '../../../constants/result-colors';
import { renderStatusCell, statusCellProps } from './resultTableCell';
import {
  getScoreTableRowHalf1,
  getScoreTableRowHalf2,
  getTotalScore,
} from '../../../utils/ohat-score-utils';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// 見出しも印刷でつぶれないよう、太字にせず大きめの文字で表示する。
// 英語の長い単語でも列幅を超えないよう、単語の途中で折り返せるようにする。
const renderTitle = (q: Question, lang?: string) => {
  return (
    <Space.Compact orientation="vertical">
      <span
        style={{ fontSize: '13px', fontWeight: 400, overflowWrap: 'anywhere' }}
      >
        {q.key}
      </span>
      <span
        style={{ fontSize: '14px', fontWeight: 400, overflowWrap: 'anywhere' }}
      >
        {q.getLabel(lang)}
      </span>
    </Space.Compact>
  );
};

// tableLayout="fixed" で列幅を固定し、表が親幅を超えないようにする。
// 横型と同じく、ラベル列は日付「2026/09/19」や「Total 10pt」が1行に収まる幅にし、
// 残りを4列で均等に割る。モバイルは画面幅が狭いため、横型より大きい割合を取る。
const LABEL_COLUMN_PERCENT = 20;
const COLUMNS_PER_ROW = 4;

const renderColumn = (q: Question, i: number, width: string, lang?: string) => {
  return {
    title: renderTitle(q, lang),
    dataIndex: i,
    key: q.key,
    width,
    align: 'center' as const,
    // 背景色はセル全体に敷くため onCell で指定する。
    onCell: statusCellProps(i),
    render: renderStatusCell(lang),
  };
};

// ラベルの色を取得する。「今回」はオレンジ（レーダーチャートと同色）、「前回」「初回」はグレー。
const getLabelColor = (label: string): string | undefined => {
  if (label === 'thisTime') return RESULT_COLORS.thisTime;
  if (label === 'previous') return RESULT_COLORS.previous;
  if (label === 'first') return RESULT_COLORS.firstLabel;
  return undefined;
};

// ラベルセル内に合計スコア・評価者名を併記する（値を持つ行のみ）。
// 太字・極細は印刷でつぶれる／かすれるため、ウェイトは400で統一し色だけで区別する。
const makeRenderLabelColumn =
  (t: TFunction) => (text: string, record: any) => {
    const color = record?.colorKey ? getLabelColor(record.colorKey) : undefined;
    const isThisTime = record?.colorKey === 'thisTime';
    // 「今回」等（日付なし）は '.' を含まない。前回/初回は `ラベル.日付`。
    const hasDate = text.includes('.');
    const [label, date] = hasDate ? text.split('.') : [text, undefined];
    return (
      <Space.Compact orientation="vertical">
        <span
          style={{
            fontWeight: 400,
            fontSize: '15px',
            color,
            overflowWrap: 'anywhere',
          }}
        >
          {label}
        </span>
        {date && (
          <span
            style={{
              fontWeight: 400,
              fontSize: '12px',
              overflowWrap: 'anywhere',
            }}
          >
            {date}
          </span>
        )}
        {typeof record?.total === 'number' && (
          <span
            style={{
              fontWeight: 400,
              fontSize: '14px',
              color: isThisTime ? RESULT_COLORS.thisTime : '#595959',
              overflowWrap: 'anywhere',
            }}
          >
            {/* nowrap だと列幅を押し広げるため使わない。「合計」と点数が
                別の行に分かれないよう、間だけ NBSP でつなぐ。 */}
            {`${t('ohat.total')}\u00a0${record.total}pt`}
          </span>
        )}
        {record?.evaluatorName && (
          <span
            style={{
              fontWeight: 400,
              fontSize: '12px',
              color: '#8c8c8c',
              overflowWrap: 'anywhere',
            }}
          >
            {record.evaluatorName}
          </span>
        )}
      </Space.Compact>
    );
  };

type Props = {
  answers: Answers;
  oldAnswers: Answers[];
};
export default function OhatResultVerticalTable(props: Props) {
  const { answers, oldAnswers } = props;
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // 上段・下段は独立したテーブルとして読まれるため、ラベルセルの内容
  //（合計・評価者名）は両方に同じものを表示する。
  const data1 = useMemo(() => {
    const [first, latest] = oldAnswers;
    return [
      Object.assign(
        {
          label: t('ohat.thisTime'),
          colorKey: 'thisTime',
          total: getTotalScore(answers),
          evaluatorName: answers.evaluatorName,
        },
        getScoreTableRowHalf1('thisScore', answers),
      ),
      latest &&
        Object.assign(
          {
            label: `${t('ohat.previous')}.${latest.evaluatedAt}`,
            colorKey: 'previous',
            total: getTotalScore(latest),
            evaluatorName: latest.evaluatorName,
          },
          getScoreTableRowHalf1('latestScore', latest),
        ),
      first &&
        Object.assign(
          {
            label: `${t('ohat.first')}.${first.evaluatedAt}`,
            colorKey: 'first',
            total: getTotalScore(first),
            evaluatorName: first.evaluatorName,
          },
          getScoreTableRowHalf1('firstScore', first),
        ),
    ].filter(Boolean);
  }, [answers, oldAnswers, t]);

  const data2 = useMemo(() => {
    const [first, latest] = oldAnswers;
    return [
      Object.assign(
        {
          label: t('ohat.thisTime'),
          colorKey: 'thisTime',
          total: getTotalScore(answers),
          evaluatorName: answers.evaluatorName,
        },
        getScoreTableRowHalf2('thisScore', answers),
      ),
      latest &&
        Object.assign(
          {
            label: `${t('ohat.previous')}.${latest.evaluatedAt}`,
            colorKey: 'previous',
            total: getTotalScore(latest),
            evaluatorName: latest.evaluatorName,
          },
          getScoreTableRowHalf2('latestScore', latest),
        ),
      first &&
        Object.assign(
          {
            label: `${t('ohat.first')}.${first.evaluatedAt}`,
            colorKey: 'first',
            total: getTotalScore(first),
            evaluatorName: first.evaluatorName,
          },
          getScoreTableRowHalf2('firstScore', first),
        ),
    ].filter(Boolean);
  }, [answers, oldAnswers, t]);

  // 上段・下段の行数は同じなので、ラベル列の有無も共通。
  const hasLabelColumn = data1.length > 1;
  const dataColumnWidth = `${
    (100 - (hasLabelColumn ? LABEL_COLUMN_PERCENT : 0)) / COLUMNS_PER_ROW
  }%`;
  const dataColumns1 = [Question.Q1, Question.Q2, Question.Q3, Question.Q4].map(
    (q, i) => renderColumn(q, i, dataColumnWidth, lang),
  );
  const dataColumns2 = [Question.Q5, Question.Q6, Question.Q7, Question.Q8].map(
    (q, i) => renderColumn(q, i, dataColumnWidth, lang),
  );
  const labelColumn = {
    title: '',
    dataIndex: 'label',
    key: 'label',
    width: `${LABEL_COLUMN_PERCENT}%`,
    align: 'center' as const,
    rowScope: 'row' as const,
    render: makeRenderLabelColumn(t),
  };

  return (
    <div style={{ margin: 20 }}>
      <Table
        dataSource={data1}
        columns={hasLabelColumn ? [labelColumn, ...dataColumns1] : dataColumns1}
        tableLayout="fixed"
        bordered
        pagination={false}
        className={'ant-slim-table'}
        size="small"
        style={{ paddingBottom: 3 }}
      />
      <Table
        dataSource={data2}
        columns={hasLabelColumn ? [labelColumn, ...dataColumns2] : dataColumns2}
        tableLayout="fixed"
        bordered
        pagination={false}
        className={'ant-slim-table'}
        size="small"
      />
    </div>
  );
}
